import * as React from "react"
import { DataType, SortingMode } from "ka-table/enums"
import { Table } from "ka-table"
import Icon from "@common/display/Icon"
import EditExternalUser from "./EditExternalUser"
import { removeExternalUserApi, useExternalUsers, ExternalUser } from "src/services/users"
import { useMutation, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import NiceModal from "@ebay/nice-modal-react"
import ConfirmDialog from "@common/display/ConfirmModal/ConfirmModal"
import SlidingPane from "react-sliding-pane"
import "react-sliding-pane/dist/react-sliding-pane.css"

interface Props {
  projects?: Array<{ code: string; name: string }>
  portfolios?: Array<{ id: string; name: string }>
}

export default function ExternalUsersList({ projects = [], portfolios = [] }: Props) {
  const { data, isLoading, isError, isFetching } = useExternalUsers()
  const [editingUser, setEditingUser] = React.useState(null)

  const queryClient = useQueryClient()

  const mutation = useMutation(removeExternalUserApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(["externalUsers"])
      toast.success("Successfully removed external user!")
    },
    onError: (error) => {
      toast.error("Something went wrong while removing the user!")
    },
  })

  const handleEdit = (user: any) => {
    setEditingUser(user)
  }

  const handleDelete = (userId: string) => {
    NiceModal.show(ConfirmDialog, {
      size: "md",
      title: "Delete confirmation",
      message: <div>Are you sure you want to remove this user?</div>,
      confirmText: "Confirm",
      onConfirm: () => mutation.mutateAsync(userId),
    })
  }

  const getProjectAccessDisplay = (user: ExternalUser) => {
    if (!user.projectAccess) return "Unrestricted"

    const { restrictProjects, projects, portfolios } = user.projectAccess

    if (restrictProjects === "no") return "Unrestricted"
    if (restrictProjects === "yes") {
      if (!projects || projects.length === 0) return "No projects"
      return `${projects.length} project${projects.length > 1 ? "s" : ""}`
    }
    if (restrictProjects === "portfolios") {
      if (!portfolios || portfolios.length === 0) return "No portfolios"
      return `${portfolios.length} portfolio${portfolios.length > 1 ? "s" : ""}`
    }

    return "Unknown"
  }

  if (isError) return <div>Error fetching external users.</div>
  if (isLoading) return <div>Loading...</div>

  return (
    <div
      style={{ height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}
      className="dark tw-bg-background tw-text-foreground"
    >
      <h2 style={{ textAlign: "center", marginTop: "8px" }}>External Users</h2>

      <div
        className="custom-theme-demo external-users-table-container"
        style={{
          height: "100%",
          overflow: "auto",
          marginTop: "16px",
          maxWidth: "calc(100% - 20px)",
        }}
      >
        <Table
          loading={{
            enabled: isFetching,
            text: "Refetching data...",
          }}
          columns={[
            { key: "userId", title: "User ID", dataType: DataType.String, width: 100 },
            { key: "email", title: "Email", dataType: DataType.String, width: 200 },
            { key: "firstName", title: "First Name", dataType: DataType.String, width: 120 },
            { key: "lastName", title: "Last Name", dataType: DataType.String, width: 120 },
            { key: "userType", title: "User Type", dataType: DataType.String, width: 120 },
            { key: "organization", title: "Organization", dataType: DataType.String, width: 150 },
            { key: "projectAccess", title: "Project Access", dataType: DataType.String, width: 140 },
            { key: "actions", title: "Actions", width: 80 },
          ]}
          data={data}
          rowKeyField={"userId"}
          noData={{
            text: "No external users found",
          }}
          sortingMode={SortingMode.Single}
          childComponents={{
            cellText: {
              content: ({ column, rowData }) => {
                switch (column.key) {
                  case "userType":
                    return rowData.userType.name
                  case "organization":
                    return rowData.organization.name
                  case "projectAccess":
                    return getProjectAccessDisplay(rowData)
                  case "actions":
                    return (
                      <div style={{ display: "flex", justifyContent: "space-around" }}>
                        <Icon icon="edit" style={{ cursor: "pointer" }} onClick={() => handleEdit(rowData)} />
                        <Icon
                          icon="delete"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDelete(rowData.userId)}
                        />
                      </div>
                    )
                  default:
                    return undefined
                }
              },
            },
          }}
        />
      </div>

      <SlidingPane
        isOpen={editingUser !== null}
        from="right"
        width="600px"
        onRequestClose={() => setEditingUser(null)}
        hideHeader
      >
        {editingUser && (
          <EditExternalUser
            user={editingUser}
            onClose={() => setEditingUser(null)}
            projects={projects}
            portfolios={portfolios}
          />
        )}
      </SlidingPane>
    </div>
  )
}
